import React, { useMemo } from 'react';
import { Send, Clock, GitBranch, ArrowDownCircle, CircleHelp } from 'lucide-react';
import { resolveExtensionIcon } from './extensionIcons';
import Tooltip from './ui/Tooltip';
import { recordFeature } from '../services/telemetry';
import { panelKeyFor } from '../services/telemetry/features';
import { useExtensionStore } from '../extensions/store';
import { aggregateContributions } from '../extensions/registry';
import { mergeActivityBarOrder } from '../extensions/activityBarRegistry';
import { useChatStore, selectTotalUnread } from '../store/chatStore';
import { useMailCatcherStore, selectMailUnread } from '../store/mailCatcherStore';
import { useAuthStore } from '../store/authStore';
import { DESKTOP_ONLY_ACTIVITY_IDS, isWebApp } from '../platform/target';
import { openHelp, useHelpStore } from '../help/helpStore';
import { useOnboardingStore } from '../onboarding/onboardingState';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useGitSyncStore } from '../store/gitSyncStore';
import { railMark } from '../services/git/gitSyncStatus';
import { withFollowers } from '../onboarding/railIds';
import { API_CLIENT_TOOLS } from '../extensions/apiClientSwitch';
import useApiClientGate from '../extensions/useApiClientGate';

// Rail ids pinned to the bottom group instead of the main tab list.
const FOOTER_ITEM_IDS = new Set(['chat.activity', 'calendar.activity']);

// Page tabs pinned to the END of the top group, in this order. Boards and
// Documents are the broad "workspace" surfaces rather than request-shaped
// tools, so they close out the top list instead of sitting mid-rail. Kept
// here rather than as manifest `order.after` hints because those chain off
// each other — one extension disabled and the chain silently reorders.
// Projects sits above Boards (owner, 14.09.2026: a project holds boards,
// documents and the rest, so it reads first); Graph stays last.
const PRIMARY_TAIL_IDS = ['projects.activity', 'task-manager.activity', 'documents.activity', 'vault.activity', 'graph.activity'];

// Exported: the Settings sidebar's per-extension sub-items reuse the same
// icon resolution as the activity bar rail.
export { resolveExtensionIcon };

/**
 * Left vertical icon rail for switching the primary view. Built-in tabs
 * (collections | history | teams | admin) keep their exact prior behavior —
 * only the ordering/rendering layer is unified with extension-contributed
 * icons via a single `.map()`, so extensions can slot in without this file
 * needing per-extension branches.
 */
/** Rail badge text for an unread count: null when zero, "99+" when capped. */
export function railBadge(count) {
  if (!count || count <= 0) return null;
  return count > 99 ? '99+' : count;
}

export default function ActivityBar({
  activeTab,
  onChange,
  sidebarPosition,
  showNavTooltips,
  t,
  updateAvailable = false,
  onOpenUpdate,
}) {
  const side = sidebarPosition === 'right' ? 'left' : 'right';

  const extensions = useExtensionStore((s) => s.extensions);
  const enabled = useExtensionStore((s) => s.enabled);
  // Rail ids this account hid (onboarding role / Settings → Your tools). Only
  // the rail filters them: the panels stay mounted and reachable by shortcut.
  const railHidden = useExtensionStore((s) => s.railHidden);
  const apiClientEnabled = useExtensionStore((s) => s.apiClientEnabled);
  useApiClientGate();
  const helpOpen = useHelpStore((s) => s.open);
  const helpPulse = useOnboardingStore((s) => s.railHelpPulse);
  const chatUnread = useChatStore(selectTotalUnread);
  // Mail catcher: unread captured messages, like the chat count (owner, 08.09.2026).
  const mailUnread = useMailCatcherStore(selectMailUnread);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Git page dot: changes waiting on any synced workspace; red when a sync failed.
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const gitByWorkspace = useGitSyncStore((s) => s.byWorkspace);
  const gitScopes = useGitSyncStore((s) => s.linkedScopes);
  const gitMark = useMemo(() => railMark(workspaces, gitByWorkspace, gitScopes), [workspaces, gitByWorkspace, gitScopes]);

  const { activityBarEntries, panelsByActivityBarId } = useMemo(() => {
    const entries = Object.keys(extensions).map((id) => ({
      manifest: extensions[id],
      enabled: !!enabled[id],
    }));
    return aggregateContributions(entries);
  }, [extensions, enabled]);

  const items = useMemo(() => {
    const builtins = [
      {
        id: 'collections',
        kind: 'builtin',
        title: t('sidebar.apiClient', 'API Client'),
        Icon: Send,
        visible: true,
        badge: null,
      },
      {
        id: 'history',
        kind: 'builtin',
        title: t('sidebar.history'),
        Icon: Clock,
        visible: true,
        badge: null,
      },
      {
        id: 'git',
        kind: 'builtin',
        title: t('sidebar.git', 'Git'),
        Icon: GitBranch,
        visible: true,
        badge: gitMark ? `dot:${gitMark}` : null,
      },
      // Teams was moved out of the icon rail into the title bar (between the
      // notifications icon and the account avatar). Admin Dashboard likewise
      // lives in the account menu (owner-only). Both remain ordering anchors in
      // BUILTIN_ORDER but contribute no rail button here.
    ];

    const order = mergeActivityBarOrder(activityBarEntries);
    const byId = new Map();
    for (const b of builtins) byId.set(b.id, b);
    for (const entry of activityBarEntries) {
      byId.set(entry.id, {
        id: entry.id,
        kind: 'extension',
        extensionId: entry.extensionId,
        panelId: panelsByActivityBarId.get(entry.id)?.id || null,
        title: entry.title,
        Icon: resolveExtensionIcon(entry.icon),
        // Chat requires a signed-in account (DMs/channels are all server-owned).
        visible: entry.id === 'chat.activity' ? isAuthenticated : true,
        badge: railBadge(
          entry.id === 'chat.activity' ? chatUnread : entry.id === 'mail-catcher.activity' ? mailUnread : 0
        ),
      });
    }
    // The web app has no devtools (they need the local machine).
    const web = isWebApp();
    // History goes with the API Client (railIds.RAIL_FOLLOWERS); both are gone
    // while the API Client is switched off (extensions/apiClientSwitch.js).
    const hidden = withFollowers(railHidden);
    if (apiClientEnabled === false) API_CLIENT_TOOLS.forEach((id) => hidden.add(id));
    return order
      .map((id) => byId.get(id))
      .filter((item) => item && item.visible && !hidden.has(item.id) && !(web && DESKTOP_ONLY_ACTIVITY_IDS.has(item.id)));
  }, [activityBarEntries, panelsByActivityBarId, t, chatUnread, mailUnread, isAuthenticated, railHidden, apiClientEnabled, gitMark]);

  // Chat and Calendar are the two "always-on companions" of the rail — you dip
  // into them alongside whatever page you are actually working in, rather than
  // switching to them as your primary view. They sit as one group at the foot
  // of the rail so the page tabs above stay a single uncluttered list. The
  // ordering hints in the manifests still decide the order *within* the group.
  const { primaryItems, footerItems } = useMemo(() => {
    const primary = [];
    const tail = [];
    const footer = [];
    for (const item of items) {
      if (FOOTER_ITEM_IDS.has(item.id)) footer.push(item);
      else if (PRIMARY_TAIL_IDS.includes(item.id)) tail.push(item);
      else primary.push(item);
    }
    tail.sort((a, b) => PRIMARY_TAIL_IDS.indexOf(a.id) - PRIMARY_TAIL_IDS.indexOf(b.id));
    return { primaryItems: [...primary, ...tail], footerItems: footer };
  }, [items]);

  const targetTabFor = (item) => (item.kind === 'extension' ? item.panelId : item.id);

  const handleClick = (item) => {
    const target = targetTabFor(item);
    if (!target) return;
    // Identifier-free usage count: this build opened this panel today. Unknown
    // ids (a marketplace extension) map to null and are dropped.
    recordFeature(panelKeyFor(item.id));
    if (item.kind === 'extension') {
      useExtensionStore.getState().activatePanel(target);
    }
    onChange(target);
  };

  const btnClass = (item) =>
    `app-icon-rail-btn${activeTab === targetTabFor(item) ? ' app-icon-rail-btn--active' : ''}`;

  const renderItem = (item) => (
    <Tooltip key={item.id} content={item.title} side={side} disabled={!showNavTooltips}>
      <button
        type="button"
        onClick={() => handleClick(item)}
        className={btnClass(item)}
        aria-label={item.title}
      >
        <item.Icon size={16} />
        {item.badge && String(item.badge).startsWith('dot:') ? (
          <span
            className={`app-icon-rail-badge app-icon-rail-badge--dot${item.badge === 'dot:error' ? ' app-icon-rail-badge--error' : ''}`}
            data-testid={`rail-dot-${item.id}`}
            aria-hidden="true"
          />
        ) : item.badge ? (
          <span className="app-icon-rail-badge" aria-hidden="true">
            {item.badge}
          </span>
        ) : null}
      </button>
    </Tooltip>
  );

  return (
    <div className="flex-shrink-0 bg-gray-50 dark:bg-[#111113] flex flex-col items-center pt-1 pb-4 space-y-2 app-icon-rail">
      {primaryItems.map(renderItem)}

      {/* A real flex spacer, NOT mt-auto: the container's `space-y-2` sets
          margin-top on every sibling via `.space-y-2 > * ~ *`, which outranks
          `.mt-auto` on specificity and silently cancels it. The growing spacer
          is immune to that, and gives the rail its justify-between shape. */}
      <div className="flex-1" aria-hidden="true" />

      {/* Bottom section: the chat/calendar group, plus the update indicator.
          No rule or separator — the gap alone does the grouping. */}
      <div className="flex flex-col items-center space-y-2 app-icon-rail-footer">
        {footerItems.map(renderItem)}

        {/* Help & docs: not a tab either — it opens a modal over whatever
            page is showing, so it never takes the active highlight from one. */}
        <Tooltip content={t?.('help.railTooltip') ?? 'Help & docs'} side={side} disabled={!showNavTooltips}>
          <button
            type="button"
            onClick={() => openHelp()}
            className={`app-icon-rail-btn app-icon-rail-btn--help${helpOpen ? ' app-icon-rail-btn--active' : ''}${helpPulse ? ' app-icon-rail-btn--pulse' : ''}`}
            aria-label={t?.('help.railTooltip') ?? 'Help & docs'}
          >
            <CircleHelp size={16} />
          </button>
        </Tooltip>

        {/* Not a TAB. It is ambient — always available, never the thing you came
            to the rail to do — and it renders only when there is an update, so
            the rail does not grow a permanently dim icon that means nothing
            most of the time. */}
        {updateAvailable && (
          <Tooltip content={t?.('update.badgeLabel') ?? 'Update available'} side={side} disabled={!showNavTooltips}>
            <button
              type="button"
              onClick={onOpenUpdate}
              className="app-icon-rail-btn app-icon-rail-btn--update"
              aria-label={t?.('update.badgeLabel') ?? 'Update available'}
            >
              <ArrowDownCircle size={16} />
              <span className="app-icon-rail-badge app-icon-rail-badge--dot" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
